import React, { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { Database, RefreshCw, Play, CheckCircle2, AlertTriangle, Loader2, Cloud, CloudOff } from "lucide-react";
import { toast } from "sonner";

/**
 * BackupPanel — pagina semplice per Admin PrimeVision con stato backup,
 * pulsante "Esegui backup ora", elenco ultimi backup con checksum/size.
 * GDPR by design: nessun accesso ai contenuti clinici, solo manifest.
 */
export default function BackupPanel() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [testingCloud, setTestingCloud] = useState(false);
  const [cloudTest, setCloudTest] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/backup/status"),
        api.get("/admin/backup/list", { params: { limit: 30 } }),
      ]);
      setStatus(s.data);
      setItems(l.data.items || []);
    } catch (e) {
      toast.error(apiErr(e, "Errore caricamento backup"));
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const runBackup = async () => {
    if (!window.confirm("Eseguire un backup manuale ora? Verrà cifrato e archiviato.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      if (data.ok !== false) {
        toast.success(`Backup completato: ${data.filename} (${(data.size_bytes / 1024).toFixed(1)} KB)${data.cloud_uploaded ? " · uploaded to cloud ☁️" : ""}`);
        await refresh();
      } else {
        toast.error(`Backup fallito: ${data.error || "errore sconosciuto"}`);
      }
    } catch (e) { toast.error(apiErr(e, "Errore backup")); }
    finally { setRunning(false); }
  };

  const testCloud = async () => {
    setTestingCloud(true);
    setCloudTest(null);
    try {
      const { data } = await api.post("/admin/backup/test-cloud");
      setCloudTest(data);
      if (data.ok) toast.success(`Cloud OK: ${data.bucket} (${data.uploaded_bytes} byte upload+head+delete)`);
      else if (data.missing_env) toast.error(`Cloud non configurato: mancano ${data.missing_env.join(", ")}`);
      else toast.error(`Cloud test fallito: ${data.error || "errore sconosciuto"}`);
    } catch (e) { toast.error(apiErr(e, "Errore test cloud")); }
    finally { setTestingCloud(false); }
  };

  const cfg = status?.config || {};
  const lastB = status?.last_backup;
  const cloudOn = cfg.cloud_storage_set;

  return (
    <div className="space-y-4" data-testid="admin-backup-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Database size={20} className="text-brand-azure" />
          <h2 className="font-extrabold text-brand-navy text-xl">Backup PrimeVision</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={refresh} disabled={loading} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-brand-navy px-3 py-2 rounded-lg border border-[#E2E8F0] hover:bg-slate-50" data-testid="backup-refresh">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Aggiorna
          </button>
          <button onClick={testCloud} disabled={testingCloud} className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-azure hover:text-brand-navyDark px-3 py-2 rounded-lg border border-brand-azure/40 hover:bg-brand-azure/5" data-testid="backup-test-cloud">
            {testingCloud ? <Loader2 size={12} className="animate-spin" /> : (cloudOn ? <Cloud size={12} /> : <CloudOff size={12} />)} Test cloud
          </button>
          <button onClick={runBackup} disabled={running || !cfg.encryption_key_set} className="inline-flex items-center gap-1.5 bg-brand-navy hover:bg-brand-navyDark text-white font-bold text-xs px-3 py-2 rounded-lg disabled:opacity-50" data-testid="backup-run-now">
            {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Esegui backup ora
          </button>
        </div>
      </div>

      {/* Banner cloud */}
      {!cloudOn && (
        <div className="bg-red-50 border border-red-300 rounded-xl p-4" data-testid="backup-cloud-missing-banner">
          <div className="flex items-start gap-2">
            <CloudOff size={18} className="text-red-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-extrabold text-sm text-red-900">Cloud backup non configurato</div>
              <div className="text-xs text-red-800 mt-1">
                I backup vengono cifrati e salvati in locale ma <b>non vengono caricati su cloud</b>. Per soddisfare il requisito di backup giornaliero off-site,
                configurare in <b>Emergent → Segreti</b> i seguenti env (compatibile con AWS S3, Cloudflare R2, Backblaze B2, MinIO, Wasabi):
              </div>
              <ul className="mt-2 text-[11px] text-red-900 list-disc list-inside font-mono">
                {(cfg.missing_env || []).length > 0
                  ? cfg.missing_env.map((k) => <li key={k}>{k}  <span className="text-red-600 not-italic">(mancante)</span></li>)
                  : (cfg.required_env || []).map((k) => <li key={k}>{k}</li>)
                }
                <li className="text-red-700">(opzionali) {(cfg.optional_env || []).join(", ")}</li>
              </ul>
              <div className="text-[10px] text-red-700 mt-2 italic">Premi <b>Test cloud</b> per verificare la configurazione una volta inseriti i secret.</div>
            </div>
          </div>
        </div>
      )}
      {cloudOn && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-xl px-4 py-2 text-xs flex items-center gap-2" data-testid="backup-cloud-ok-banner">
          <Cloud size={14} className="text-emerald-700" /> <b className="text-emerald-900">Cloud backup configurato</b> · bucket <span className="font-mono">{cfg.bucket_masked}</span> · endpoint <span className="font-mono">{cfg.endpoint}</span> · prefix <span className="font-mono">{cfg.prefix}</span>
        </div>
      )}
      {cloudTest && (
        <div className={`rounded-xl px-4 py-3 text-xs ${cloudTest.ok ? "bg-emerald-50 border border-emerald-300 text-emerald-900" : "bg-amber-50 border border-amber-300 text-amber-900"}`} data-testid="backup-cloud-test-result">
          {cloudTest.ok ? (
            <span>✅ Test cloud OK · bucket <b className="font-mono">{cloudTest.bucket}</b> · endpoint <span className="font-mono">{cloudTest.endpoint}</span> · {cloudTest.uploaded_bytes} byte upload+head+delete · {new Date(cloudTest.tested_at).toLocaleString("it-IT")}</span>
          ) : cloudTest.missing_env ? (
            <span>⚠️ Mancano i secret: <b className="font-mono">{cloudTest.missing_env.join(", ")}</b></span>
          ) : (
            <span>❌ Errore upload: {cloudTest.error}</span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Stato configurazione</div>
          <div className="mt-2 text-sm space-y-1">
            <div className="flex items-center gap-2">
              {cfg.encryption_key_set ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-amber-500" />}
              <span>Cifratura AES-256: <b>{cfg.encryption_key_set ? "configurata" : "MANCANTE — setta BACKUP_ENCRYPTION_KEY"}</b></span>
            </div>
            <div className="flex items-center gap-2">
              {cfg.cloud_storage_set ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-red-500" />}
              <span>Cloud storage: <b className={cfg.cloud_storage_set ? "" : "text-red-700"}>{cfg.cloud_storage_set ? "configurato" : `MANCANTI ${(cfg.missing_env||[]).join(', ')} — solo backup locale`}</b></span>
            </div>
            <div className="flex items-center gap-2">
              {cfg.include_pdfs ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-slate-400" />}
              <span>Includi PDF referti: <b>{cfg.include_pdfs ? `sì (max ${cfg.pdf_limit})` : "no"}</b></span>
            </div>
          </div>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Schedulazione</div>
          <div className="mt-2 text-sm space-y-1">
            <div>Cron: <b className="font-mono">{cfg.schedule || "—"}</b></div>
            <div>Retention: <b>{cfg.retention_days || 30}gg</b> giornalieri + <b>{cfg.retention_months || 12}</b> mensili</div>
            <div className="text-[10px] text-slate-500">Local dir: <span className="font-mono">{cfg.local_dir || "—"}</span></div>
          </div>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Ultimo backup</div>
          {lastB ? (
            <div className="mt-2 text-sm space-y-1">
              <div className="font-mono text-xs">{lastB.filename}</div>
              <div>Size: <b>{((lastB.size_bytes || 0) / 1024).toFixed(1)} KB</b></div>
              <div>Quando: <b>{new Date(lastB.created_at).toLocaleString("it-IT")}</b></div>
              <div className="text-[10px] text-slate-500 font-mono break-all">SHA: {(lastB.sha256 || "").slice(0, 16)}…</div>
            </div>
          ) : (
            <div className="mt-2 text-sm text-slate-500 italic">Nessun backup ancora eseguito</div>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Ultimi backup ({items.length})</h3>
        {items.length === 0 ? (
          <div className="text-sm text-slate-500 italic">Nessun backup in archivio.</div>
        ) : (
          <table className="w-full text-xs" data-testid="backup-list-table">
            <thead className="text-slate-500">
              <tr className="border-b border-slate-100">
                <th className="text-left font-bold py-2">Data</th>
                <th className="text-left font-bold py-2">File</th>
                <th className="text-right font-bold py-2">Size</th>
                <th className="text-left font-bold py-2">Triggered by</th>
                <th className="text-left font-bold py-2">SHA-256</th>
                <th className="text-center font-bold py-2">Cloud</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-1.5">{new Date(b.created_at).toLocaleString("it-IT")}</td>
                  <td className="py-1.5 font-mono">{b.filename}</td>
                  <td className="py-1.5 text-right">{((b.size_bytes || 0) / 1024).toFixed(1)} KB</td>
                  <td className="py-1.5">{b.triggered_by}</td>
                  <td className="py-1.5 font-mono text-[10px]">{(b.sha256 || "").slice(0, 12)}…</td>
                  <td className="py-1.5 text-center">{b.cloud_uploaded ? "✅" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[10px] text-slate-500 italic">
        I backup sono cifrati AES-256-GCM con PBKDF2-SHA256 (300k iter). I dati clinici NON sono accessibili da questa pagina:
        sono visibili solo i manifest tecnici (filename, dimensione, checksum, conteggio record).
      </div>
    </div>
  );
}
